/**
 * 患者信息归一化工具
 *
 * 统一患者基础信息的字段命名和数据类型，确保患者信息在不同模块间的一致性。
 *
 * @module clinical-record/shared/utils/patientInfoNormalizer
 */
import {
  getRecordPatientId,
  isRecordPatientIdentityAligned,
} from "@clinical-record/shared/domain/currentMedicalContext";
import {
  PATIENT_FIELD_ALIAS_MAP,
  PATIENT_FIELD_SPECS,
  normalizePatientAge,
  normalizePatientGender,
  type PatientGender,
} from "@patient-management/shared/domain/patientStandard";

// ==================== 字段名称映射 ====================

/** 患者基础信息字段名称映射（用于统一读取和写入） */
export const PATIENT_FIELD_MAPPING = Object.freeze(
  Object.fromEntries(
    PATIENT_FIELD_SPECS.map((spec) => [spec.camelKey, spec.key]),
  ),
) as Readonly<Record<string, string>>;

/**
 * 病历中的患者展示快照字段（只读，保存时删除）。
 * patient_id 是 health_records 指向患者的外键，不属于展示快照，必须保留。
 */
export const RECORD_PATIENT_FIELDS = new Set<string>([
  "patient_name",
  "patient_gender",
  "patient_age",
  "patient_phone",
  "patient_id_card",
]);

/** 患者信息字段别名（用于兼容旧代码） */
export const PATIENT_FIELD_ALIASES: Readonly<Record<string, string>> =
  Object.freeze({
    ...Object.fromEntries(
      Object.entries(PATIENT_FIELD_ALIAS_MAP).map(([alias, key]) => [
        alias,
        PATIENT_FIELD_SPECS.find((spec) => spec.key === key)?.camelKey ?? key,
      ]),
    ),
    patient_phone: "phone",
    patient_id_card: "idCard",
    patientGender: "gender",
    patientAge: "age",
    sex: "gender",
  });

// ==================== 类型定义 ====================

export type { PatientGender };

export interface NormalizedPatientInfo {
  name: string;
  gender?: PatientGender;
  age?: number | string; // string 用于显示 "XX岁"，number 用于计算
  phone?: string;
  idCard?: string;
  email?: string;
  address?: string;
  birthDate?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  organization?: string;
  patientType?: string;
}

// ==================== 工具函数 ====================

/**
 * 从患者信息中提取单个字段的值（支持别名）
 */
export function getPatientField(
  source: Record<string, unknown>,
  fieldName: string,
): unknown {
  // 优先使用标准字段名
  if (source[fieldName] !== undefined && source[fieldName] !== null) {
    return source[fieldName];
  }

  // 尝试别名
  for (const [alias, target] of Object.entries(PATIENT_FIELD_ALIASES)) {
    if (
      target === fieldName &&
      source[alias] !== undefined &&
      source[alias] !== null
    ) {
      return source[alias];
    }
  }

  return undefined;
}

/**
 * 归一化性别值
 */
export function normalizeGender(gender: unknown): PatientGender {
  return normalizePatientGender(gender);
}

/**
 * 归一化年龄值（返回数字）
 */
export function normalizeAge(age: unknown): number | undefined {
  return normalizePatientAge(age);
}

/**
 * 由出生日期派生周岁。
 *
 * 患者主档通常只存 ``birth_date``（``health_records`` 则不存年龄），
 * 因此展示年龄必须能从出生日期派生；支持 ``YYYY-MM-DD`` 与完整 ISO 时间串，
 * 非法日期或超出合理区间（<0 或 >=150）返回 undefined。
 */
export function calculateAgeFromBirthDate(
  value: unknown,
  now: Date = new Date(),
): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const birth = new Date(year, month - 1, day);
  if (
    Number.isNaN(birth.getTime()) ||
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return undefined;
  }

  let age = now.getFullYear() - year;
  const birthdayNotReached =
    now.getMonth() < month - 1 ||
    (now.getMonth() === month - 1 && now.getDate() < day);
  if (birthdayNotReached) age -= 1;

  return age >= 0 && age < 150 ? age : undefined;
}

/**
 * 格式化年龄显示（返回 "XX岁"）
 */
export function formatAgeDisplay(age: unknown): string {
  const normalized = normalizeAge(age);
  return normalized !== undefined ? `${normalized}岁` : "未知年龄";
}

/**
 * 从任意来源归一化患者信息
 */
export function normalizePatientInfo(
  source: Record<string, unknown>,
): NormalizedPatientInfo {
  const result: Partial<NormalizedPatientInfo> = {};

  // 提取各个字段
  const name = getPatientField(source, "name");
  if (name) result.name = String(name);

  const gender = getPatientField(source, "gender");
  if (gender) result.gender = normalizeGender(gender);

  const age = getPatientField(source, "age");
  if (age !== undefined) result.age = normalizeAge(age);

  const phone = getPatientField(source, "phone");
  if (phone) result.phone = String(phone);

  const idCard = getPatientField(source, "idCard");
  if (idCard) result.idCard = String(idCard);

  const email = getPatientField(source, "email");
  if (email) result.email = String(email);

  const address = getPatientField(source, "address");
  if (address) result.address = String(address);

  const birthDate = getPatientField(source, "birthDate");
  if (birthDate) result.birthDate = String(birthDate);

  const emergencyContact = getPatientField(source, "emergencyContact");
  if (emergencyContact) result.emergencyContact = String(emergencyContact);

  const emergencyPhone = getPatientField(source, "emergencyPhone");
  if (emergencyPhone) result.emergencyPhone = String(emergencyPhone);

  const organization = getPatientField(source, "organization");
  if (organization) result.organization = String(organization);

  const patientType = getPatientField(source, "patientType");
  if (patientType) result.patientType = String(patientType);

  return result as NormalizedPatientInfo;
}

/**
 * 从患者对象和病历对象合并患者信息
 * 优先级：patient > record
 */
export function mergePatientInfo(
  patient: Record<string, unknown> | null | undefined,
  record: Record<string, unknown> | null | undefined,
): NormalizedPatientInfo {
  const merged: Record<string, unknown> = {};

  // 仅允许同一稳定 ID 的患者覆盖病历快照；无病历 ID 时保持旧兼容行为。
  const canUsePatient = Boolean(
    patient &&
      (!record ||
        !getRecordPatientId(record) ||
        isRecordPatientIdentityAligned(patient, record)),
  );
  if (canUsePatient && patient) {
    Object.assign(merged, patient);
  }

  // 合并病历中的患者信息（仅作为后备）
  if (record) {
    for (const field of RECORD_PATIENT_FIELDS) {
      const standardField = PATIENT_FIELD_ALIASES[field];
      if (
        standardField &&
        !merged[standardField] &&
        record[field] !== undefined &&
        record[field] !== null
      ) {
        merged[standardField] = record[field];
      }
    }
  }

  return normalizePatientInfo(merged);
}

/**
 * 清理病历数据中的患者冗余字段
 */
export function removeRecordPatientFields<T extends Record<string, unknown>>(
  data: T,
): Omit<T, typeof RECORD_PATIENT_FIELDS extends Set<infer K> ? K : never> {
  const cleaned = { ...data };
  for (const field of RECORD_PATIENT_FIELDS) {
    delete cleaned[field];
  }
  return cleaned as Omit<
    T,
    typeof RECORD_PATIENT_FIELDS extends Set<infer K> ? K : never
  >;
}

/**
 * 生成患者显示文本（用于报告等场景）
 */
export function formatPatientDisplay(info: NormalizedPatientInfo): string {
  const parts: string[] = [];

  if (info.name) {
    parts.push(info.name);
  }

  if (info.gender) {
    parts.push(info.gender);
  }

  if (info.age !== undefined) {
    parts.push(formatAgeDisplay(info.age));
  }

  return parts.length > 0 ? parts.join("，") : "未知患者";
}

/** 业务代码统一从本模块读取患者/病历快照。 */
export const patientInfoNormalizer = {
  getMongoId(source: object | null | undefined): string {
    if (!source) return "";
    const record = source as Record<string, unknown>;
    return String(record.id || record._id || "");
  },
  getId(source: object | null | undefined): string {
    if (!source) return "";
    const record = source as Record<string, unknown>;
    return (
      getRecordPatientId(record) ||
      String(
        record.patient_id || record.patientId || record.id || record._id || "",
      )
    );
  },
  getName(source: object | null | undefined): string {
    return source
      ? String(getPatientField(source as Record<string, unknown>, "name") || "")
      : "";
  },
  getGender(source: object | null | undefined): PatientGender | "" {
    if (!source) return "";
    const value = getPatientField(source as Record<string, unknown>, "gender");
    return value === undefined || value === null || value === ""
      ? ""
      : normalizeGender(value);
  },
  getAge(source: object | null | undefined): number | undefined {
    if (!source) return undefined;
    const record = source as Record<string, unknown>;
    const explicit = normalizeAge(getPatientField(record, "age"));
    if (explicit !== undefined) return explicit;
    // 病历不存年龄，主档常只有出生日期：缺年龄时由 birth_date 派生
    return calculateAgeFromBirthDate(getPatientField(record, "birthDate"));
  },
  getPhone(source: object | null | undefined): string {
    return source
      ? String(
          getPatientField(source as Record<string, unknown>, "phone") || "",
        )
      : "";
  },
  getIdCard(source: object | null | undefined): string {
    return source
      ? String(
          getPatientField(source as Record<string, unknown>, "idCard") || "",
        )
      : "";
  },
  extractFromRecord(source: Record<string, unknown>): NormalizedPatientInfo {
    return normalizePatientInfo(source);
  },
  normalize(
    source: Record<string, unknown> | null | undefined,
  ): Record<string, unknown> {
    if (!source) return {};
    return {
      ...source,
      id: this.getMongoId(source),
      patient_id: this.getId(source),
      ...normalizePatientInfo(source),
    };
  },
  normalizeArray(
    sources: Array<Record<string, unknown> | null | undefined>,
  ): Record<string, unknown>[] {
    return sources.map((source) => this.normalize(source));
  },
  hasPatientInfo(source: object | null | undefined): boolean {
    return Boolean(
      source &&
        (this.getId(source) || this.getMongoId(source)) &&
        this.getName(source),
    );
  },
  validate(source: object | null | undefined): {
    valid: boolean;
    missing: string[];
  } {
    const missing: string[] = [];
    if (!source) return { valid: false, missing: ["患者对象为空"] };
    if (!this.getId(source) && !this.getMongoId(source)) missing.push("患者ID");
    if (!this.getName(source)) missing.push("患者姓名");
    return { valid: missing.length === 0, missing };
  },
  mergeInto<T extends Record<string, unknown>>(
    target: T,
    info: Record<string, unknown>,
  ): T {
    const normalized = normalizePatientInfo(info);
    Object.assign(target, normalized);
    return target;
  },
};
